import React from 'react';

import Advantage from 'pages/Landings/components/Advantage';
import Advantages from 'pages/Landings/components/Advantages';
import {
    CodeIcon,
    DownloadsIcon,
    McpForAiAgentsIcon,
    MoleculeIcon,
    PatternIcon
} from 'pages/Landings/components/Icons';
import Paragraph from 'pages/Landings/components/Paragraph';
import TopicContent from 'pages/Landings/components/TopicContent';
import TopicHeading from 'pages/Landings/components/TopicHeading';
import TopicTemplate from 'pages/Landings/components/TopicTemplate';

const McpForAiAgents: React.FC = () => {
    return (
        <TopicTemplate
            heading={
                <TopicHeading
                    title="Vulners MCP for AI Agents"
                    description="Ship Trustworthy Security Answers, Fast"
                    illustration={<McpForAiAgentsIcon />}
                />
            }
            content={
                <TopicContent>
                    <Paragraph
                        primary="Ship Trustworthy Security Answers, Fast"
                        secondary="Vulners MCP gives your agents the full vulnerability picture on demand - without teaching them the Vulners API or juggling parameters. The agent sends a CVE ID or a product + version, and MCP returns a compact fact set plus a map of related document IDs from the Vulners database. Your model then expands only what it needs - advisories, patches, PoCs, KEV entries, NVD records, vendor notes—keeping prompts lean and answers verifiable."
                    />
                    <Paragraph
                        primary="Why the Document Map Matters (and Kills Hallucinations)"
                        secondary={
                            <>
                                {
                                    'LLMs will answer even when they shouldn’t. MCP forces a better pattern: ID → facts → evidence map → citations.'
                                }
                                <ul>
                                    <li>
                                        {
                                            'Answers anchored to sources: Each claim can point to a concrete document ID (vendor advisory, KEV item, PoC repo, distro notice). If there’s no doc, the agent has nothing to cite - nudging it to say “no evidence” instead of inventing details.'
                                        }
                                    </li>
                                    <li>
                                        {
                                            'Selective expansion: The model expands only the documents it needs from the map, which means fewer stray web searches and fewer made-up references.'
                                        }
                                    </li>
                                    <li>
                                        {
                                            'Consistent provenance: Every document handle is stable and typed (e.g., vendor advisory vs. exploit reference), so your prompts can require evidence per claim class.'
                                        }
                                    </li>
                                    <li>
                                        {
                                            'Conflict-aware reasoning: When sources disagree (e.g., vendor vs. NVD), the agent can fetch both IDs and present a reconciled view with citations - no guesswork.'
                                        }
                                    </li>
                                    <li>
                                        {
                                            'Token discipline: Facts first, docs on demand. You minimize prompt bloat while maximizing reliability and traceability.'
                                        }
                                    </li>
                                </ul>
                            </>
                        }
                    />
                    <Paragraph
                        primary="Fast Time-to-Context & Minimal Wiring"
                        secondary={
                            <>
                                {'Plug MCP into your agent once. From then on, the flow is simple:'}
                                <ul>
                                    <li>{'Ask: “CVE-2025-XXXX” or “vendor product 3.2.1”.'}</li>
                                    <li>
                                        {
                                            'Get: normalized facts (CVSS vectors, EPSS, KEV, exploit presence) and a graph of related document IDs to pull on demand.'
                                        }
                                    </li>
                                    <li>
                                        {
                                            'No bespoke ETL, no schema guessing - just ID-in → context & document handles-out, ready for citations and follow-ups.'
                                        }
                                    </li>
                                </ul>
                            </>
                        }
                    />
                    <Paragraph
                        primary="Built for Product & Corporate Teams"
                        secondary={
                            <ul>
                                <li>
                                    {
                                        'Product teams: In-app copilots, PR annotators, and customer-facing chat that cite vendor advisories and PoCs with links users trust—without bloating prompts.'
                                    }
                                </li>
                                <li>
                                    {
                                        'Corporate teams: Incident triage, ticket routing, and asset impact checks that retrieve the exact docs (Patch Tuesday notes, distro advisories, KEV items) relevant to your environment.'
                                    }
                                </li>
                            </ul>
                        }
                    />
                </TopicContent>
            }
            advantages={
                <Advantages
                    title={
                        <>
                            {'Why Vulners MCP?'}
                            <br />
                            {'Grounded, Deterministic, Efficient'}
                        </>
                    }
                >
                    <Advantage
                        icon={<PatternIcon />}
                        title="Simple ask pattern"
                        description="The model only needs an ID (CVE or product+version). MCP handles the query logic and returns stable document IDs for expansion."
                    />
                    <Advantage
                        icon={<DownloadsIcon />}
                        title="Whole-picture sources"
                        description="CVE/NVD, CISA KEV, vendor & distro advisories (Linux and Microsoft), exploit/PoC streams (including first-party GitHub/Gitee), plus security blogs/news—already correlated."
                    />
                    <Advantage
                        icon={<CodeIcon />}
                        title="Deterministic JSON for agents"
                        description="Consistent fields and relationships keep tool calls stable across model versions; fewer retries, cleaner prompts."
                    />
                    <Advantage
                        icon={<MoleculeIcon />}
                        title="Token-smart by design"
                        description="Facts first, documents on demand—so answers stay current and concise, with provenance your users can check."
                    />
                </Advantages>
            }
        />
    );
};

export default McpForAiAgents;
